<script setup lang="ts">
import EpIcon from '~/components/icon/EpIcon.vue'
import type { ITab } from '~/types/tab.type'

interface IProps {
  tabList: Array<ITab>
  textColor?: string
  textOpacity?: number | string
  border?: boolean
  bgColor?: string
  bgOpacity?: number | string
  hoverBgColor?: string
  hoverBgOpacity?: number | string
  activeTextColor?: string
  activeBgColor?: string
  borderColor?: string
  fixWidth?: number | string
  backgroundBlur?: boolean
  bold?: boolean
}

const props = withDefaults(defineProps<IProps>(), {
  border: false,
  textColor: 'default',
  textOpacity: 100,
  bgColor: 'default',
  bgOpacity: 0,
  hoverBgColor: 'default',
  hoverBgOpacity: 10,
  activeTextColor: 'default',
  activeBgColor: 'primary',
  borderColor: 'primary',
  backgroundBlur: false,
  bold: false
})

const emits = defineEmits([ 'clickTab' ])

const defaultTextColor = computed(() => {
  if (props.textOpacity === 100) {
    return `text-${props.textColor}`
  }
  return `text-${props.textColor}/${props.textOpacity}`
})

const defaultBgColor = computed(() => {
  if (props.bgOpacity === 0) {
    return 'bg-transparent'
  }
  return `bg-${props.bgColor}/${props.bgOpacity}`
})

const hoverTabColor = computed(() => {
  return `hover:bg-${props.hoverBgColor}/${props.hoverBgOpacity} hover:text-${props.textColor}`
})

const defaultTabColor = computed(() => {
  return `${defaultTextColor.value} ${defaultBgColor.value} ${hoverTabColor.value}`
})

const activeTabColor = computed(() => {
  return `text-${props.activeTextColor} bg-${props.activeBgColor}`
})

const tabBorder = computed(() => {
  return `border border-${props.borderColor}`
})

const tabWidth = computed(() => {
  if (!props.fixWidth) {
    return null
  }
  return `${props.fixWidth}px`
})
</script>

<template>
  <ul
    class="z-10 flex gap-x-1 bg-transparent"
    :class="[backgroundBlur && 'backdrop-blur-[32px]']"
  >
    <li
      v-for="tab in tabList"
      :key="tab.label"
    >
      <button
        class="tab-item"
        :class="[tab.isActive ? activeTabColor : defaultTabColor, border && tabBorder, bold && 'font-bold']"
        @click="emits('clickTab', tab.value)"
      >
        <EpIcon
          v-if="tab.iconName"
          :name="tab.iconName"
          :size="20"
        />
        <span v-if="tab.label">{{ tab.label }}</span>
      </button>
    </li>
  </ul>
</template>

<style scoped lang="scss">
.tab-item {
  @apply p-2 text-sm flex justify-center items-center gap-x-2 transition-all duration-300 ease-[ease];
  width: v-bind(tabWidth);
}
</style>